import { fmtMins, timeToMinutes, fmtClock } from '../lib/format.js';

export function ScheduleGantt({ schedule, isToday, statusBadge, loading, error }) {
  // Title reflects the date context: "Today's staffing" / "Past staffing" /
  // "Planned staffing" — Square Labor returns the same shape for all three.
  const title = statusBadge === 'live' ? "Today's staffing"
              : statusBadge === 'planning' ? 'Planned staffing'
              : 'Staffing (past)';

  if (loading) {
    return (
      <div className="card">
        <div style={{ fontSize: 13, fontWeight: 700, color: '#212121', marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 12, color: '#757575' }}>Loading schedule…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="card" style={{ borderLeft: '4px solid #c62828' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#c62828', marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 12, color: '#37474f' }}>{error}</div>
      </div>
    );
  }
  if (!schedule || !Array.isArray(schedule.shifts) || schedule.shifts.length === 0) {
    return (
      <div className="card">
        <div style={{ fontSize: 13, fontWeight: 700, color: '#212121', marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 12, color: '#757575' }}>No shifts scheduled.</div>
      </div>
    );
  }

  // Spec §3.5 — time axis 5 AM through 9 PM (16-hour span).
  const AXIS_START = 5 * 60;          // 5:00
  const AXIS_END   = 21 * 60;         // 21:00
  const AXIS_SPAN  = AXIS_END - AXIS_START;
  const ROW_H = 28;
  const tickHours = [5, 7, 9, 11, 13, 15, 17, 19, 21];

  const STATUS_STYLE = {
    pending:        { bg: '#cfd8dc', border: 'transparent', text: '#37474f', label: '' },
    on_time:        { bg: '#a5d6a7', border: 'transparent', text: '#1b5e20', label: '' },
    late:           { bg: '#ffe082', border: '#ef6c00',     text: '#e65100', label: 'late' },
    not_clocked_in: { bg: '#ffcdd2', border: '#c62828',     text: '#c62828', label: 'no-clock' },
    overtime:       { bg: '#ffe082', border: '#ef6c00',     text: '#e65100', label: 'OT' },
    clocked_out:    { bg: '#cfd8dc', border: 'transparent', text: '#455a64', label: '', dim: true },
  };

  const shifts = schedule.shifts.slice().sort((a, b) => {
    const sa = timeToMinutes(a.scheduledStart) ?? 0;
    const sb = timeToMinutes(b.scheduledStart) ?? 0;
    return sa - sb;
  });

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#212121' }}>
          {title}
          <span style={{ fontWeight: 400, color: '#757575', fontSize: 12, marginLeft: 8 }}>
            {isToday && schedule.staffOnClock != null
              ? `${schedule.staffOnClock} of ${schedule.staffScheduled} on clock`
              : `${schedule.staffScheduled || shifts.length} scheduled`}
          </span>
        </div>
        <div style={{ fontSize: 11, color: '#9e9e9e' }}>Schedule from Square Labor</div>
      </div>

      <div style={{ position: 'relative', paddingLeft: 110, paddingRight: 8 }}>
        {/* hour ticks behind the rows */}
        <div style={{ position: 'absolute', left: 110, right: 8, top: 0, bottom: 22, pointerEvents: 'none' }}>
          {tickHours.map((h) => {
            const left = ((h * 60 - AXIS_START) / AXIS_SPAN) * 100;
            return (
              <div key={h} style={{ position: 'absolute', left: `${left}%`, top: 0, bottom: 0,
                                    borderLeft: '1px dashed #eeeeee' }} />
            );
          })}
        </div>

        <div style={{ position: 'relative' }}>
          {shifts.map((s, i) => {
            const start = timeToMinutes(s.scheduledStart);
            const end   = timeToMinutes(s.scheduledEnd);
            if (start == null || end == null) return null;
            const left  = Math.max(0, ((start - AXIS_START) / AXIS_SPAN) * 100);
            const width = Math.max(2, ((end - start) / AXIS_SPAN) * 100);
            const stat  = STATUS_STYLE[s.clockStatus] || STATUS_STYLE.pending;
            return (
              <div key={i} style={{ position: 'relative', height: ROW_H, marginBottom: 4 }}>
                {/* name column */}
                <div style={{ position: 'absolute', left: -110, top: 4, width: 100, fontSize: 12, color: '#37474f',
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {s.isOpenShift ? <em style={{ color: '#9e9e9e' }}>Open shift</em> : s.employeeName}
                </div>
                {/* shift bar */}
                <div title={`${s.employeeName || 'Open'} · ${s.scheduledStart}–${s.scheduledEnd} · ${s.clockStatus}`}
                  style={{
                    position: 'absolute', top: 4, left: `${left}%`, width: `${width}%`, height: ROW_H - 8,
                    background: s.isOpenShift ? '#eceff1' : (s.roleColor || stat.bg),
                    border: `${s.clockStatus === 'not_clocked_in' ? '1.5px dashed' : '1px solid'} ${stat.border}`,
                    borderRadius: 4,
                    color: '#fff',
                    fontSize: 11, fontWeight: 600,
                    padding: '2px 6px',
                    boxShadow: `inset 0 0 0 100px ${stat.bg}`,
                    opacity: stat.dim ? 0.55 : 1,
                    overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
                  }}>
                  <span style={{ color: stat.text }}>
                    {fmtClock(s.scheduledStart)}–{fmtClock(s.scheduledEnd)}
                  </span>
                  {stat.label && (
                    <span style={{ background: stat.border || '#9e9e9e', color: '#fff', padding: '0 6px',
                                  borderRadius: 8, fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>
                      {stat.label}
                      {s.clockStatus === 'late' && s.lateMinutes ? ` ${fmtMins(s.lateMinutes)}` : ''}
                      {s.clockStatus === 'overtime' && s.overtimeMinutes ? ` ${s.overtimeMinutes}m` : ''}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          {/* hour labels along the bottom */}
          <div style={{ position: 'relative', height: 18, marginTop: 4 }}>
            {tickHours.map((h) => {
              const left = ((h * 60 - AXIS_START) / AXIS_SPAN) * 100;
              const lab = h === 12 ? '12p' : h > 12 ? `${h - 12}p` : `${h}a`;
              return (
                <span key={h} style={{ position: 'absolute', left: `${left}%`, transform: 'translateX(-50%)',
                                      fontSize: 10, color: '#9e9e9e' }}>{lab}</span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
